import { TILE, VIEW_H, VIEW_W } from '../config/config';
import type { Camera } from '../world/camera';
import type { TileMap } from '../world/tilemap';
import { T } from '../world/tiles';
import { Font } from './font';
import { C, THEMES, type ThemeName } from './palette';
import type { PixelBuf } from './pixel';
import type { Sprites } from './sprites';

export interface DecorItem {
  type: string;
  x: number;
  y: number;
  /** Parallax factor: 1 = locked to world, 0.5 = drifts at half speed. */
  parallax?: number;
}

/**
 * All drawing goes through here. Entities hand over world coordinates and the renderer
 * converts to screen space using the active camera, so nothing else needs to know
 * about scrolling.
 */
export class Renderer {
  readonly ctx: CanvasRenderingContext2D;
  readonly font = new Font();
  cam!: Camera;
  /** Extra vertical offset used for whole-screen effects. */
  offsetY = 0;

  constructor(
    readonly canvas: HTMLCanvasElement,
    readonly sprites: Sprites,
  ) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D canvas unavailable');
    ctx.imageSmoothingEnabled = false;
    this.ctx = ctx;
  }

  setCamera(cam: Camera): void {
    this.cam = cam;
  }

  get w(): number {
    return VIEW_W;
  }

  get h(): number {
    return VIEW_H;
  }

  private get camX(): number {
    return this.cam ? this.cam.renderX : 0;
  }

  private get camY(): number {
    return (this.cam ? this.cam.renderY : 0) - this.offsetY;
  }

  clear(color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, VIEW_W, VIEW_H);
  }

  /** Vertical gradient sky in 8px bands (keeps the retro banding look). */
  drawSky(theme: ThemeName): void {
    const t = THEMES[theme];
    const bands = 14;
    for (let i = 0; i < bands; i++) {
      const f = i / (bands - 1);
      this.ctx.fillStyle = mix(t.skyTop, t.skyBottom, f);
      this.ctx.fillRect(0, Math.floor((i * VIEW_H) / bands), VIEW_W, Math.ceil(VIEW_H / bands));
    }
    if (t.underground) {
      // sparse mineral speckles, stable per world position
      const camX = this.camX;
      for (let i = 0; i < 60; i++) {
        const wx = i * 61 + ((i * 37) % 23) * 7;
        const sx = wx - camX * 0.35;
        const mx = ((sx % (VIEW_W + 32)) + VIEW_W + 32) % (VIEW_W + 32);
        const wy = 12 + ((i * 53) % (VIEW_H - 40));
        this.ctx.fillStyle = i % 3 === 0 ? C.crystal : C.cavesoilLight;
        this.ctx.fillRect(Math.round(mx - 16), wy, 1, 1);
      }
    }
  }

  drawDecor(items: readonly DecorItem[]): void {
    for (const d of items) {
      const buf = this.sprites.get(`decor.${d.type}`);
      const p = d.parallax ?? 1;
      const sx = Math.round(d.x - this.camX * p);
      const sy = Math.round(d.y - this.camY * (p === 1 ? 1 : 1));
      if (sx + buf.w < -8 || sx > VIEW_W + 8) continue;
      this.ctx.drawImage(buf.canvas, sx, sy);
    }
  }

  /**
   * Draw the visible slice of the tile grid.
   * `bumpAt` returns a pixel offset for blocks that are currently popping up.
   */
  drawTiles(
    map: TileMap,
    theme: ThemeName,
    frame: number,
    bumpAt?: (tx: number, ty: number) => number,
  ): void {
    const camX = this.camX;
    const camY = this.camY;
    const x0 = Math.max(0, Math.floor(camX / TILE));
    const x1 = Math.min(map.w - 1, Math.floor((camX + VIEW_W) / TILE));
    const y0 = Math.max(0, Math.floor(camY / TILE));
    const y1 = Math.min(map.h - 1, Math.floor((camY + VIEW_H) / TILE));

    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const id = map.get(tx, ty);
        if (id === T.EMPTY) continue;
        const key = tileSpriteKey(map, tx, ty, id, theme, frame);
        if (!key) continue;
        const buf = this.sprites.get(key);
        const dy = bumpAt ? bumpAt(tx, ty) : 0;
        this.ctx.drawImage(
          buf.canvas,
          Math.round(tx * TILE - camX),
          Math.round(ty * TILE - camY - dy),
        );
      }
    }
  }

  /** Draw a baked sprite at a world position. */
  blit(buf: PixelBuf, wx: number, wy: number): void {
    this.ctx.drawImage(buf.canvas, Math.round(wx - this.camX), Math.round(wy - this.camY));
  }

  sprite(name: string, wx: number, wy: number, faceRight = true): void {
    this.blit(this.sprites.facing(name, faceRight), wx, wy);
  }

  /** Upside-down blit, used for defeated enemies tumbling away. */
  blitFlipY(buf: PixelBuf, wx: number, wy: number): void {
    const x = Math.round(wx - this.camX);
    const y = Math.round(wy - this.camY);
    this.ctx.save();
    this.ctx.translate(x, y + buf.h);
    this.ctx.scale(1, -1);
    this.ctx.drawImage(buf.canvas, 0, 0);
    this.ctx.restore();
  }

  /** Blit only part of a sprite (used to hide enemies still inside a pipe). */
  blitClipped(buf: PixelBuf, wx: number, wy: number, srcY: number, srcH: number): void {
    if (srcH <= 0) return;
    this.ctx.drawImage(
      buf.canvas,
      0,
      srcY,
      buf.w,
      srcH,
      Math.round(wx - this.camX),
      Math.round(wy - this.camY),
      buf.w,
      srcH,
    );
  }

  rectWorld(x: number, y: number, w: number, h: number, color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(Math.round(x - this.camX), Math.round(y - this.camY), Math.ceil(w), Math.ceil(h));
  }

  frameWorld(x: number, y: number, w: number, h: number, color: string): void {
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(
      Math.round(x - this.camX) + 0.5,
      Math.round(y - this.camY) + 0.5,
      Math.round(w) - 1,
      Math.round(h) - 1,
    );
  }

  rectScreen(x: number, y: number, w: number, h: number, color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(Math.round(x), Math.round(y), Math.ceil(w), Math.ceil(h));
  }

  textWorld(
    text: string,
    wx: number,
    wy: number,
    color: string,
    align: 'left' | 'center' | 'right' = 'left',
  ): void {
    this.font.draw(this.ctx, text, wx - this.camX, wy - this.camY, color, align);
  }

  text(
    text: string,
    x: number,
    y: number,
    color: string = C.hud,
    align: 'left' | 'center' | 'right' = 'left',
  ): void {
    this.font.draw(this.ctx, text, x, y, color, align);
  }

  textShadow(
    text: string,
    x: number,
    y: number,
    color: string = C.hud,
    align: 'left' | 'center' | 'right' = 'left',
  ): void {
    this.font.drawShadowed(this.ctx, text, x, y, color, C.hudShade, align);
  }

  /** Full-screen colour wash (fade in/out, damage flash). */
  fade(alpha: number, color = '#000000'): void {
    if (alpha <= 0) return;
    this.ctx.globalAlpha = Math.min(1, alpha);
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, VIEW_W, VIEW_H);
    this.ctx.globalAlpha = 1;
  }
}

export function tileSpriteKey(
  map: TileMap,
  tx: number,
  ty: number,
  id: number,
  theme: ThemeName,
  frame: number,
): string | null {
  const p = `tile.${theme}.`;
  switch (id) {
    case T.GROUND:
      return map.get(tx, ty - 1) === T.GROUND ? `${p}ground.mid` : `${p}ground.top`;
    case T.ROCK:
      return `${p}rock`;
    case T.CRYSTAL:
      return `${p}crystal`;
    case T.BRICK:
    case T.BRICK_COINS:
    case T.BRICK_ITEM:
      return `${p}brick`;
    case T.QUESTION:
    case T.QUESTION_ITEM:
    case T.QUESTION_LIFE:
      return `${p}question${frame % 3}`;
    case T.USED:
      return `${p}used`;
    case T.HIDDEN_COIN:
    case T.HIDDEN_LIFE:
      return null;
    case T.PIPE_TL:
      return `${p}pipe.tl`;
    case T.PIPE_TR:
      return `${p}pipe.tr`;
    case T.PIPE_BL:
      return `${p}pipe.bl`;
    case T.PIPE_BR:
      return `${p}pipe.br`;
    case T.PIPE_MOUTH_T:
      return `${p}pipe.mt`;
    case T.PIPE_MOUTH_B:
      return `${p}pipe.mb`;
    case T.PIPE_SHAFT_T:
      return `${p}pipe.st`;
    case T.PIPE_SHAFT_B:
      return `${p}pipe.sb`;
    case T.PLANK:
      return `${p}plank`;
    case T.FLAGPOLE:
      return `${p}pole`;
    case T.FLAG_TOP:
      return `${p}poletop`;
    case T.CASTLE:
      return `${p}castle`;
    default:
      return null;
  }
}

function mix(a: string, b: string, t: number): string {
  const pa = parseHex(a);
  const pb = parseHex(b);
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * t);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * t);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * t);
  return `rgb(${r},${g},${bl})`;
}

function parseHex(h: string): [number, number, number] {
  const v = parseInt(h.slice(1), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}
